// Advanced Edit Modal - Professional media editing for social content
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    X,
    Type,
    Image as ImageIcon,
    Palette,
    RotateCw,
    Download
} from 'lucide-react';

function AdvancedEditModal({ isOpen, onClose, media, onApplyChanges }) {
    // ✅ FIX: All hooks MUST be called before any early returns
    const [textOverlays, setTextOverlays] = useState([]);
    const [selectedOverlay, setSelectedOverlay] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoPosition, setLogoPosition] = useState({ x: 10, y: 10 });
    const [filters, setFilters] = useState({
        brightness: 100,
        contrast: 100,
        saturation: 100,
        blur: 0
    });
    const [cropMode, setCropMode] = useState(false);
    const [rotation, setRotation] = useState(0);

    const canvasRef = useRef(null);
    const logoInputRef = useRef(null);
    const mediaRef = useRef(null);

    // Enhanced font options with proper CSS font stacks
    const fonts = [
        { name: 'Arial', value: 'Arial, sans-serif' },
        { name: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
        { name: 'Times New Roman', value: '"Times New Roman", Times, serif' },
        { name: 'Georgia', value: 'Georgia, serif' },
        { name: 'Impact', value: 'Impact, "Arial Black", sans-serif' },
        { name: 'Courier New', value: '"Courier New", Courier, monospace' },
        { name: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
        { name: 'Verdana', value: 'Verdana, sans-serif' },
        { name: 'Comic Sans MS', value: '"Comic Sans MS", cursive' },
        { name: 'Palatino', value: 'Palatino, serif' }
    ];

    // Font weight options
    const fontWeights = [
        { name: 'Light', value: '300' },
        { name: 'Normal', value: '400' },
        { name: 'Medium', value: '500' },
        { name: 'Semibold', value: '600' },
        { name: 'Bold', value: '700' },
        { name: 'Black', value: '900' }
    ];

    // Enhanced canvas drawing with shadows
    const drawCanvas = useCallback(() => {
        if (!canvasRef.current || !mediaRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Apply transformations
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(rotation * Math.PI / 180);

        // Apply filters
        ctx.filter = `brightness(${filters.brightness}%) contrast(${filters.contrast}%) saturate(${filters.saturation}%) blur(${filters.blur}px)`;

        // Draw main image
        ctx.drawImage(mediaRef.current, -canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height);

        ctx.restore();

        // Draw text overlays with enhanced styling
        textOverlays.forEach((overlay, index) => {
            ctx.save();

            // Set font with weight
            const fontWeight = overlay.fontWeight || '400';
            ctx.font = `${fontWeight} ${overlay.fontSize || 24}px ${overlay.fontFamily || 'Arial'}`;
            ctx.fillStyle = overlay.color || '#ffffff';
            ctx.textAlign = overlay.textAlign || 'left';

            // Apply shadow if enabled
            if (overlay.shadowEnabled) {
                ctx.shadowColor = overlay.shadowColor || 'rgba(0,0,0,0.8)';
                ctx.shadowBlur = overlay.shadowBlur || 4;
                ctx.shadowOffsetX = overlay.shadowOffsetX || 2;
                ctx.shadowOffsetY = overlay.shadowOffsetY || 2;
            }

            // Draw stroke if enabled
            if (overlay.strokeEnabled && overlay.strokeWidth > 0) {
                ctx.strokeStyle = overlay.strokeColor || '#000000';
                ctx.lineWidth = overlay.strokeWidth || 2;
                ctx.lineJoin = 'round';
                ctx.strokeText(overlay.text, overlay.x, overlay.y);
            }

            // Draw fill text
            ctx.fillText(overlay.text, overlay.x, overlay.y);

            ctx.restore();
        });

        // Draw logo if present
        if (logoFile && logoPosition) {
            const logoImg = new Image();
            logoImg.onload = () => {
                const logoSize = 80; // Fixed logo size
                ctx.drawImage(logoImg, logoPosition.x, logoPosition.y, logoSize, logoSize);
            };
            logoImg.src = URL.createObjectURL(logoFile);
        }
    }, [rotation, filters, textOverlays, logoFile, logoPosition]);

    // Load media into canvas
    useEffect(() => {
        console.log('AdvancedEditModal - media prop:', media);
        if (media && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            if (media.type === 'image') {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                    console.log('Image loaded successfully:', media.url);
                    canvas.width = img.width;
                    canvas.height = img.height;
                    drawCanvas();
                };
                img.onerror = (error) => {
                    console.error('Failed to load image:', media.url, error);
                };
                console.log('Setting image src:', media.url);
                img.src = media.url;
                mediaRef.current = img;
            }
        }
    }, [media, drawCanvas]);

    // Redraw canvas when any property changes
    useEffect(() => {
        if (canvasRef.current && mediaRef.current) {
            drawCanvas();
        }
    }, [filters, textOverlays, logoFile, logoPosition, rotation, drawCanvas]);


    // Add text overlay with enhanced default properties
    const addTextOverlay = () => {
        const newOverlay = {
            id: Date.now(),
            text: editingText || 'Sample Text',
            x: 50,
            y: 100,
            fontSize: 32,
            fontFamily: 'Arial, sans-serif',
            fontWeight: '700',
            color: '#ffffff',
            textAlign: 'left',
            strokeEnabled: true,
            strokeColor: '#000000',
            strokeWidth: 3,
            shadowEnabled: true,
            shadowColor: 'rgba(0,0,0,0.8)',
            shadowBlur: 4,
            shadowOffsetX: 2,
            shadowOffsetY: 2
        };
        setTextOverlays([...textOverlays, newOverlay]);
        setSelectedOverlay(newOverlay.id);
        setEditingText('');
        drawCanvas();
    };

    // Update text overlay
    const updateTextOverlay = (id, updates) => {
        setTextOverlays(overlays =>
            overlays.map(overlay =>
                overlay.id === id ? { ...overlay, ...updates } : overlay
            )
        );
        setTimeout(drawCanvas, 0);
    };

    // Remove text overlay
    const removeTextOverlay = (id) => {
        setTextOverlays(overlays => overlays.filter(overlay => overlay.id !== id));
        setSelectedOverlay(null);
        drawCanvas();
    };

    // Handle logo upload
    const handleLogoUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setLogoFile(file);
            setTimeout(drawCanvas, 100);
        }
    };

    // Apply filter changes
    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({ ...prev, [filterType]: value }));
        setTimeout(drawCanvas, 0);
    };

    // Apply changes and return edited media
    const handleApplyChanges = () => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const editedImageUrl = canvas.toDataURL('image/jpeg', 0.9);

        // Pass just the image URL to match ImageGeneratorModal's expectation
        onApplyChanges(editedImageUrl);
        onClose(); // Close the modal after applying changes
    };

    // Get the currently selected overlay
    const currentOverlay = textOverlays.find(overlay => overlay.id === selectedOverlay);

    // small reusable styles
    const subLabel = { fontSize: '10px', color: 'var(--fg-faint)', marginBottom: '4px', display: 'block', fontFamily: 'var(--mono)', letterSpacing: '0.08em', textTransform: 'uppercase' };
    const miniInput = { width: '100%', padding: '6px 8px', background: 'var(--bg-2)', border: '1px solid var(--rule)', borderRadius: '2px', color: 'var(--fg)', fontSize: '13px', fontFamily: 'var(--sans)' };

    // ✅ Now check if modal should be shown (after all hooks)
    if (!isOpen || !media) return null;

    return (
        <div className="vault-overlay">
            <div className="vault-modal vault-modal--wide" style={{ maxWidth: '1280px', height: '95vh', maxHeight: '95vh' }}>
                {/* Header */}
                <div className="vault-modal-head">
                    <div>
                        <h2 className="vault-modal-title">Advanced Media Editor</h2>
                        <div className="vault-modal-sub">Text · Logo · Filters · Transform</div>
                    </div>
                    <div className="spacer" />
                    <button
                        onClick={onClose}
                        className="vault-icon-btn"
                        aria-label="Close"
                    >
                        <X />
                    </button>
                </div>

                <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                    {/* Tools Panel */}
                    <div style={{ width: '384px', borderRight: '1px solid var(--rule)', padding: '20px', overflowY: 'auto' }}>
                        {/* Text Overlays Section */}
                        <div style={{ marginBottom: '24px' }}>
                            <h3 className="vault-label"><Type size={12} /> Text Overlays</h3>

                            {/* Add New Text */}
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Enter text"
                                        value={editingText}
                                        onChange={(e) => setEditingText(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && addTextOverlay()}
                                        className="vault-field"
                                        style={{ flex: 1, fontSize: '13px', padding: '8px 10px' }}
                                    />
                                    <button
                                        onClick={addTextOverlay}
                                        className="vault-btn vault-btn--primary"
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>

                            {/* Text Overlays List */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {textOverlays.map((overlay, index) => (
                                    <div key={overlay.id}
                                         className={`vault-card ${selectedOverlay === overlay.id ? 'is-selected' : ''}`}
                                         style={{ display: 'block', cursor: 'pointer', padding: '12px' }}
                                         onClick={() => setSelectedOverlay(overlay.id)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: selectedOverlay === overlay.id ? '12px' : 0 }}>
                                            <span style={{ fontSize: '13px', color: 'var(--fg-dim)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {overlay.text}
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeTextOverlay(overlay.id);
                                                }}
                                                style={{ color: 'var(--danger)', background: 'transparent', border: 0, cursor: 'pointer', display: 'flex' }}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>

                                        {selectedOverlay === overlay.id && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} onClick={(e) => e.stopPropagation()}>
                                                {/* Text Content */}
                                                <div>
                                                    <label style={subLabel}>Text</label>
                                                    <input
                                                        type="text"
                                                        value={overlay.text}
                                                        onChange={(e) => updateTextOverlay(overlay.id, { text: e.target.value })}
                                                        style={miniInput}
                                                    />
                                                </div>

                                                {/* Font Family */}
                                                <div>
                                                    <label style={subLabel}>Font</label>
                                                    <select
                                                        value={overlay.fontFamily}
                                                        onChange={(e) => updateTextOverlay(overlay.id, { fontFamily: e.target.value })}
                                                        style={miniInput}
                                                    >
                                                        {fonts.map(font => (
                                                            <option key={font.name} value={font.value}>
                                                                {font.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Font Size and Weight */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                    <div>
                                                        <label style={subLabel}>Size: {overlay.fontSize}px</label>
                                                        <input
                                                            type="range"
                                                            min="12"
                                                            max="120"
                                                            value={overlay.fontSize}
                                                            onChange={(e) => updateTextOverlay(overlay.id, { fontSize: Number(e.target.value) })}
                                                            className="vault-range"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label style={subLabel}>Weight</label>
                                                        <select
                                                            value={overlay.fontWeight}
                                                            onChange={(e) => updateTextOverlay(overlay.id, { fontWeight: e.target.value })}
                                                            style={miniInput}
                                                        >
                                                            {fontWeights.map(weight => (
                                                                <option key={weight.name} value={weight.value}>
                                                                    {weight.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Text Color */}
                                                <div>
                                                    <label style={subLabel}>Text Color</label>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <input
                                                            type="color"
                                                            value={overlay.color}
                                                            onChange={(e) => updateTextOverlay(overlay.id, { color: e.target.value })}
                                                            style={{ width: '32px', height: '32px', borderRadius: '2px', border: '1px solid var(--rule)', background: 'transparent' }}
                                                        />
                                                        <input
                                                            type="text"
                                                            value={overlay.color}
                                                            onChange={(e) => updateTextOverlay(overlay.id, { color: e.target.value })}
                                                            style={{ ...miniInput, flex: 1 }}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Text Alignment */}
                                                <div>
                                                    <label style={subLabel}>Alignment</label>
                                                    <select
                                                        value={overlay.textAlign}
                                                        onChange={(e) => updateTextOverlay(overlay.id, { textAlign: e.target.value })}
                                                        style={miniInput}
                                                    >
                                                        <option value="left">Left</option>
                                                        <option value="center">Center</option>
                                                        <option value="right">Right</option>
                                                    </select>
                                                </div>

                                                {/* Stroke Settings */}
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={overlay.strokeEnabled}
                                                            onChange={(e) => updateTextOverlay(overlay.id, { strokeEnabled: e.target.checked })}
                                                            style={{ accentColor: 'var(--accent)' }}
                                                        />
                                                        <label style={{ ...subLabel, margin: 0 }}>Text Stroke</label>
                                                    </div>
                                                    {overlay.strokeEnabled && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                            <div>
                                                                <label style={subLabel}>Color</label>
                                                                <input
                                                                    type="color"
                                                                    value={overlay.strokeColor}
                                                                    onChange={(e) => updateTextOverlay(overlay.id, { strokeColor: e.target.value })}
                                                                    style={{ width: '100%', height: '24px', borderRadius: '2px', border: '1px solid var(--rule)', background: 'transparent' }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label style={subLabel}>Width: {overlay.strokeWidth}px</label>
                                                                <input
                                                                    type="range"
                                                                    min="1"
                                                                    max="10"
                                                                    value={overlay.strokeWidth}
                                                                    onChange={(e) => updateTextOverlay(overlay.id, { strokeWidth: Number(e.target.value) })}
                                                                    className="vault-range"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Shadow Settings */}
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={overlay.shadowEnabled}
                                                            onChange={(e) => updateTextOverlay(overlay.id, { shadowEnabled: e.target.checked })}
                                                            style={{ accentColor: 'var(--accent)' }}
                                                        />
                                                        <label style={{ ...subLabel, margin: 0 }}>Text Shadow</label>
                                                    </div>
                                                    {overlay.shadowEnabled && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            <div>
                                                                <label style={subLabel}>Shadow Color</label>
                                                                <input
                                                                    type="color"
                                                                    value={overlay.shadowColor?.replace('rgba(0,0,0,0.8)', '#000000') || '#000000'}
                                                                    onChange={(e) => updateTextOverlay(overlay.id, { shadowColor: e.target.value })}
                                                                    style={{ width: '100%', height: '24px', borderRadius: '2px', border: '1px solid var(--rule)', background: 'transparent' }}
                                                                />
                                                            </div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
                                                                <div>
                                                                    <label style={subLabel}>Blur: {overlay.shadowBlur}</label>
                                                                    <input
                                                                        type="range"
                                                                        min="0"
                                                                        max="20"
                                                                        value={overlay.shadowBlur}
                                                                        onChange={(e) => updateTextOverlay(overlay.id, { shadowBlur: Number(e.target.value) })}
                                                                        className="vault-range"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label style={subLabel}>X: {overlay.shadowOffsetX}</label>
                                                                    <input
                                                                        type="range"
                                                                        min="-10"
                                                                        max="10"
                                                                        value={overlay.shadowOffsetX}
                                                                        onChange={(e) => updateTextOverlay(overlay.id, { shadowOffsetX: Number(e.target.value) })}
                                                                        className="vault-range"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label style={subLabel}>Y: {overlay.shadowOffsetY}</label>
                                                                    <input
                                                                        type="range"
                                                                        min="-10"
                                                                        max="10"
                                                                        value={overlay.shadowOffsetY}
                                                                        onChange={(e) => updateTextOverlay(overlay.id, { shadowOffsetY: Number(e.target.value) })}
                                                                        className="vault-range"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Position Controls */}
                                                <div>
                                                    <label style={subLabel}>Position</label>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                        <div>
                                                            <label style={subLabel}>X: {overlay.x}</label>
                                                            <input
                                                                type="range"
                                                                min="0"
                                                                max="800"
                                                                value={overlay.x}
                                                                onChange={(e) => updateTextOverlay(overlay.id, { x: Number(e.target.value) })}
                                                                className="vault-range"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={subLabel}>Y: {overlay.y}</label>
                                                            <input
                                                                type="range"
                                                                min="0"
                                                                max="600"
                                                                value={overlay.y}
                                                                onChange={(e) => updateTextOverlay(overlay.id, { y: Number(e.target.value) })}
                                                                className="vault-range"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Logo Section */}
                        <div style={{ marginBottom: '24px' }}>
                            <h3 className="vault-label"><ImageIcon size={12} /> Logo</h3>
                            <button
                                onClick={() => logoInputRef.current?.click()}
                                className="vault-btn vault-btn--ghost"
                                style={{ width: '100%' }}
                            >
                                {logoFile ? 'Change Logo' : 'Upload Logo'}
                            </button>
                            {logoFile && (
                                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div style={subLabel}>Position</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                        <input
                                            type="range"
                                            min="0"
                                            max="300"
                                            value={logoPosition.x}
                                            onChange={(e) => setLogoPosition(prev => ({ ...prev, x: Number(e.target.value) }))}
                                            className="vault-range"
                                        />
                                        <input
                                            type="range"
                                            min="0"
                                            max="300"
                                            value={logoPosition.y}
                                            onChange={(e) => setLogoPosition(prev => ({ ...prev, y: Number(e.target.value) }))}
                                            className="vault-range"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Filters Section */}
                        <div style={{ marginBottom: '24px' }}>
                            <h3 className="vault-label"><Palette size={12} /> Filters</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {Object.entries(filters).map(([key, value]) => (
                                    <div key={key}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', ...subLabel, marginBottom: '4px' }}>
                                            <span style={{ textTransform: 'capitalize' }}>{key}</span>
                                            <span>{key === 'blur' ? value + 'px' : value + '%'}</span>
                                        </div>
                                        <input
                                            type="range"
                                            min={key === 'blur' ? 0 : 0}
                                            max={key === 'blur' ? 10 : 200}
                                            value={value}
                                            onChange={(e) => handleFilterChange(key, Number(e.target.value))}
                                            className="vault-range"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Transform Section */}
                        <div style={{ marginBottom: '24px' }}>
                            <h3 className="vault-label"><RotateCw size={12} /> Transform</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', ...subLabel, marginBottom: '4px' }}>
                                    <span>Rotation</span>
                                    <span>{rotation}°</span>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="360"
                                    value={rotation}
                                    onChange={(e) => setRotation(Number(e.target.value))}
                                    className="vault-range"
                                />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                    <button
                                        onClick={() => setRotation(prev => prev + 90)}
                                        className="vault-btn vault-btn--ghost"
                                        style={{ height: '32px', fontSize: '10px' }}
                                    >
                                        90° Right
                                    </button>
                                    <button
                                        onClick={() => setRotation(0)}
                                        className="vault-btn vault-btn--ghost"
                                        style={{ height: '32px', fontSize: '10px' }}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Canvas Area */}
                    <div style={{ flex: 1, padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-1)' }}>
                        <div style={{ maxWidth: '100%', maxHeight: '100%', overflow: 'auto' }}>
                            <canvas
                                ref={canvasRef}
                                style={{ maxWidth: '100%', maxHeight: '70vh', border: '1px solid var(--rule)', borderRadius: '4px', boxShadow: 'var(--shadow)' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="vault-modal-foot" style={{ justifyContent: 'space-between' }}>
                    <div style={{ fontSize: '11px', color: 'var(--fg-faint)', fontFamily: 'var(--mono)' }}>
                        Professional media editor with advanced text styling and effects
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                            onClick={onClose}
                            className="vault-btn vault-btn--ghost"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApplyChanges}
                            className="vault-btn vault-btn--primary"
                        >
                            <Download size={15} />
                            Apply Changes
                        </button>
                    </div>
                </div>
            </div>

            {/* Hidden file input */}
            <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
            />
        </div>
    );
}

export default AdvancedEditModal;
